import { DatasetFieldStatistics } from '../../model/analytics/dataset-field-statistics';

/**
 * Fixed display sequence for the distinct values of a field (legend / colorization order).
 * `datasetId` is optional: when set, the order applies only to that overlay, otherwise to every
 * overlay having the field. A dataset specific entry wins over a generic one for the same field.
 */
export interface FieldValueOrder {
    fieldId: string;
    datasetId?: string;
    order: string[];
}

export const FIELD_VALUE_ORDERS: FieldValueOrder[] = [
    {
        fieldId: 'bws_label',
        datasetId: 'bws',
        order: [
            'Extremely High (>80%)',
            'High (40-80%)',
            'Medium - High (20-40%)',
            'Low - Medium (10-20%)',
            'Low (<10%)',
            'Arid and Low Water Use',
            'No Data'
        ]
    },
    // TODO: replace the placeholder fieldId / datasetId of the entries below with the actual ones
    {
        // Coastal Eutrophication Potential
        fieldId: 'cep_label',
        datasetId: 'cep',
        order: [
            'Extremely High (>5)',
            'High (1 to 5)',
            'Medium - High (0 to 1)',
            'Low - Medium (-5 to 0)',
            'Low (<-5)',
            'No Data'
        ]
    },
    {
        // Coastal Flood Risk
        fieldId: 'cfr_label',
        datasetId: 'cfr',
        order: [
            'Extremely High (more than 2 in 1,000)',
            'High (3 in 10,000 to 2 in 1,000)',
            'Medium - High (7 in 100,000 to 3 in 10,000)',
            'Low - Medium (9 in 1,000,000 to 7 in 100,000)',
            'Low (0 to 9 in 1,000,000)',
            'No Data'
        ]
    },
    {
        // Drought Risk
        fieldId: 'drr_label',
        datasetId: 'drr',
        order: [
            'High (0.8-1.0)',
            'Medium - High (0.6-0.8)',
            'Medium (0.4-0.6)',
            'Low - Medium (0.2-0.4)',
            'Low (0.0-0.2)',
            'No Data'
        ]
    },
    {
        // Groundwater Table Decline
        fieldId: 'gtd_label',
        datasetId: 'gtd',
        order: [
            'Extremely High (>6 cm/y)',
            'High (4-6 cm/y)',
            'Medium - High (1-4 cm/y)',
            'Low - Medium (0-1 cm/y)',
            'Low (<0 cm/y)',
            'Insignificant Trend',
            'No Data'
        ]
    },
    {
        // Regulatory and Reputational Risk Index
        fieldId: 'rri_label',
        datasetId: 'rri',
        order: [
            'Extremely High (4-5)',
            'High (3-4)',
            'Medium - High (2-3)',
            'Low - Medium (1-2)',
            'Low (0-1)',
            'No Data'
        ]
    },
    {
        // Riverine Flood Risk
        fieldId: 'rfr_label',
        datasetId: 'rfr',
        order: [
            'Extremely High (more than 1 in 100)',
            'High (6 in 1,000 to 1 in 100)',
            'Medium - High (2 in 1,000 to 6 in 1,000)',
            'Low - Medium (1 in 1,000 to 2 in 1,000)',
            'Low (0 to 1 in 1,000)',
            'No Data'
        ]
    },
    {
        // Unimproved/No Sanitation
        fieldId: 'usa_label',
        datasetId: 'usa',
        order: [
            'Extremely High (>20%)',
            'High (10-20%)',
            'Medium - High (5-10%)',
            'Low - Medium (2.5-5%)',
            'Low (<2.5%)',
            'No Data'
        ]
    },
    {
        // Untreated Connected Wastewater
        fieldId: 'udw_label',
        datasetId: 'udw',
        order: [
            'Extremely High (100%)',
            'High (90-100%)',
            'Medium - High (60-90%)',
            'Low - Medium (30-60%)',
            'Low (<30%)',
            'No to Low Wastewater Collected',
            'No Data'
        ]
    },
    // TODO: Untreated Wastewater - legend order to be verified before implementation
    {
        // Water Depletion
        fieldId: 'bwd_label',
        datasetId: 'bwd',
        order: [
            'Extremely High (>75%)',
            'High (50-75%)',
            'Medium - High (25-50%)',
            'Low - Medium (5-25%)',
            'Low (<5%)',
            'Arid and Low Water Use',
            'No Data'
        ]
    }
];

/**
 * Severity ranking that ignores the numeric range inside a label.
 *
 * The ranges differ per indicator and per data release - gtd ships "0-2 / 2-4 / 4-8 / >8" while
 * the list above says "0-1 / 1-4 / 4-6 / >6" - and exact text matching then fails silently,
 * leaving the values unsorted AND flipping the legend. The severity prefix is stable across every
 * Aqueduct indicator, so it is the safer thing to rank on.
 */
const SEVERITY_SCALE: string[] = [
    'Extremely High',
    'High',
    'Medium - High',
    'Medium',
    'Low - Medium',
    'Low'
];

// Matched longest-first, so 'Medium - High' wins over 'High' and 'Medium', and 'Low - Medium' over
// 'Low' and 'Medium'. The rank itself still comes from SEVERITY_SCALE - match priority and severity
// order are not the same list.
const SEVERITY_MATCH_ORDER: string[] = [...SEVERITY_SCALE].sort((a, b) => b.length - a.length);

/** Non-severity buckets. Always sorted after the severity classes, in this order. */
const TRAILING_VALUES: string[] = [
    'Arid and Low Water Use',
    'Insignificant Trend',
    'No to Low Wastewater Collected',
    'No Data'
];

const UNRANKED = Number.MAX_SAFE_INTEGER;

/**
 * Position of a label on the high -> low scale, or UNRANKED when unrecognised.
 *
 * This runs for every text colorized overlay, not only Aqueduct ones, so the match is deliberately
 * strict: the label must be exactly a severity name, or that name followed by a bracketed range -
 * the shape every Aqueduct label uses ('Low (<10%)'). A bare prefix test would also claim unrelated
 * values such as 'Lowland', 'Highway' or 'Medium Density Housing' and silently reorder fields that
 * have nothing to do with risk.
 */
export function severityRank(value: any): number {
    if (typeof value !== 'string') {
        return UNRANKED;
    }
    const label = value.trim();
    const trailing = TRAILING_VALUES.findIndex(item => item.toLowerCase() === label.toLowerCase());
    if (trailing !== -1) {
        return SEVERITY_SCALE.length + trailing;
    }
    const lower = label.toLowerCase();
    const prefix = SEVERITY_MATCH_ORDER.find(item => {
        const name = item.toLowerCase();
        if (!lower.startsWith(name)) {
            return false;
        }
        const rest = lower.slice(name.length).trim();
        return rest === '' || rest.startsWith('(');
    });
    return prefix === undefined ? UNRANKED : SEVERITY_SCALE.indexOf(prefix);
}

/**
 * True when at least one value sits on the severity scale.
 *
 * Deliberately 'some' and not 'every': applyFieldValueOrder ranks what it recognises and leaves the
 * rest at the end, so the result is already in display order. Requiring every value would let a
 * single unmapped category flip the whole legend back to front.
 */
export function canRankBySeverity(values: any[]): boolean {
    const labels = (values || []).filter(value => value !== null && value !== undefined);
    return labels.length > 0 && labels.some(label => severityRank(label) !== UNRANKED);
}

/**
 * Diagnostics. Ordering here is silent by design - a miss simply leaves the values untouched -
 * which makes a drifted label hard to spot from the UI.
 *
 * Off by default. Turn it on from the browser console for a session:
 *     window.__fieldValueOrderDebug = true
 */
function isOrderLoggingOn(): boolean {
    return typeof window !== 'undefined' && (window as any).__fieldValueOrderDebug === true;
}

function logOrder(...args: any[]): void {
    if (isOrderLoggingOn()) {
        console.log('[field-value-order]', ...args);
    }
}

function warnOrder(...args: any[]): void {
    if (isOrderLoggingOn()) {
        console.warn('[field-value-order]', ...args);
    }
}

/**
 * True when the values are already in display order and must not be reversed by the legend.
 * Either an exact configured sequence covers them, or they sit on the severity scale.
 */
export function hasFieldValueOrder(values: any[], datasetId?: string): boolean {
    const labels = (values || []).filter(value => value !== null && value !== undefined);
    if (!labels.length) {
        return false;
    }
    const exact = FIELD_VALUE_ORDERS
        .filter(item => !item.datasetId || item.datasetId === datasetId)
        .find(item => labels.every(label => item.order.includes(label)));
    if (exact) {
        logOrder('order KEPT for', datasetId, '- exact config', exact.fieldId);
        return true;
    }
    if (canRankBySeverity(labels)) {
        logOrder('order KEPT for', datasetId, '- ranked by severity prefix');
        return true;
    }
    warnOrder('order REVERSED for', datasetId,
        '- nothing on the severity scale and no exact config. Values:',
        labels.filter(label => severityRank(label) === UNRANKED));
    return false;
}

/**
 * Reorders statistics values high -> low.
 *
 * Prefers an exact configured sequence when one covers the data; otherwise falls back to the
 * severity prefix, so a drifted numeric range in a label no longer breaks the ordering.
 * Values that fit neither are kept at the end in their original order.
 */
export function applyFieldValueOrder(statistics: DatasetFieldStatistics, datasetId?: string): DatasetFieldStatistics {
    if (!statistics?.values?.length) {
        return statistics;
    }
    const matches = FIELD_VALUE_ORDERS.filter(item =>
        item.fieldId === statistics.id && (!item.datasetId || item.datasetId === datasetId));
    const config = matches.find(item => item.datasetId) || matches[0];

    const unknownToConfig = config
        ? statistics.values.filter(value => config.order.indexOf(value) === -1)
        : statistics.values;

    // Use the configured sequence only when it actually covers the data; a half-matching list ranks
    // the misses equal and leaves them wherever the backend put them.
    const useConfig = !!config && unknownToConfig.length === 0;

    if (config && !useConfig) {
        warnOrder('config', config.fieldId, 'does not cover these values, using severity ranking:',
            unknownToConfig, '| configured order:', config.order);
    }
    if (!config) {
        warnOrder('no config for fieldId', statistics.id, '(overlay', datasetId + ') - using severity ranking.',
            'Configured fieldIds:', FIELD_VALUE_ORDERS.map(item => item.fieldId));
    }

    const rank = (value: string) => {
        if (useConfig) {
            const index = config.order.indexOf(value);
            return index === -1 ? config.order.length : index;
        }
        return severityRank(value);
    };

    const unrankable = statistics.values.filter(value => rank(value) === UNRANKED);
    if (unrankable.length) {
        warnOrder('these values could not be ranked and drop to the end:', unrankable);
    }

    const before = [...statistics.values];
    statistics.values = [...statistics.values].sort((a, b) => rank(a) - rank(b));
    logOrder('SORTED overlay', datasetId, '/ fieldId', statistics.id,
        'via', useConfig ? 'exact config' : 'severity prefix', { before, after: statistics.values });
    return statistics;
}
